import { CommonModule, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ListItem } from '../../../../models/api/core/list/list-item';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { Allocation } from '../../../../models/api/view/claim/financials/payment-history/allocation';
import { Export } from '../../../../models/api/view/claim/financials/payment-history/export';
import { AllocationDetails } from '../../../../models/api/view/claim/financials/payment-history/payment-allocation-details';
import {
  HistoryResponse,
  PaymentHistory,
} from '../../../../models/api/view/claim/financials/payment-history/payment-history';
import { PaymentHistoryColumn } from '../../../../models/api/view/claim/financials/payment-history/payment-history-column';
import { PaymentHistoryRow } from '../../../../models/api/view/claim/financials/payment-history/payment-history-row';
import { PaymentOffset } from '../../../../models/api/view/claim/financials/payment-history/payment-offset';
import { PaymentVendor } from '../../../../models/api/view/claim/financials/payment-history/payment-vendor';
import { PaymentAndAllocateId } from '../../../../models/api/view/claim/financials/payment-history/paymentandAllocateId';
import { Action } from '../../../../models/core/action';
import { FormatPipe } from '../../../../pipes/format.pipe';
import { ExportService } from '../../../../services/export/export.service';
import { ClaimExportMapper } from '../../../../utilities/claim-export-mapper';
import { integer } from '../../../../validators/integer';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { ExportFormat } from '../../../core/buttons/export-button.component';
import { InputComponent } from '../../../core/controls/input.component';
import { RadioButtonComponent } from '../../../core/controls/radio-button.component';
import { RangeComponent } from '../../../core/controls/range.component';
import { SelectComponent } from '../../../core/controls/select.component';
import { HelpIconComponent } from '../../../core/labels/help-icon.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { TagComponent, TagType } from '../../../core/labels/tag.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { TabPanelComponent } from '../../../core/panels/tab-panel/tab-panel.component';
import { TabComponent } from '../../../core/panels/tab-panel/tab.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { EscheatmentDetailComponent } from './escheatment-detail.component';
import { PaymentAllocationDetailComponent } from './payment-allocation-detail.component';
import { PaymentAllocationOffsetsComponent } from './payment-allocation-offsets.component';
import { PaymentDetailTabComponent } from './payment-detail-tab.component';
import { VendorPaymentDetailComponent } from './vendor-payment-detail.component';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';
@Component({
  selector: 'app-payment-history',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    PageHeaderComponent,
    SelectComponent,
    ReactiveFormsModule,
    ButtonComponent,
    ActionRowComponent,
    InputComponent,
    RangeComponent,
    DetailPanelComponent,
    DetailItemComponent,
    TagComponent,
    TabPanelComponent,
    TabComponent,
    CommonModule,
    HelpIconComponent,
    FormatPipe,
    RadioButtonComponent,
    PaymentDetailTabComponent,
    EscheatmentDetailComponent,
    VendorPaymentDetailComponent,
    PaymentAllocationDetailComponent,
    PaymentAllocationOffsetsComponent,
  ],
  template: `
    <app-page-header label="Payment history" />
    <form [formGroup]="fg">
      <div class="tw-flex">
        <app-radio-button
          [control]="payAlloc"
          label=""
          optionLabel="label"
          optionValue="key"
          [options]="payAllocOptions"
          [vertical]="false"
          (change)="onPayAlloc()"
          class="tw-basis-1/3 tw-pb-[21px]"
        ></app-radio-button>
      </div>
      <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-0">
        <app-select
          [options]="providerOptions"
          optionLabel="value"
          optionValue="key"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [virtualScroll]="false"
          [control]="fg.controls.provider"
          label="Payee"
          [multiple]="false"
        />
        @if (!disability) {
          <app-input
            class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
            [control]="fg.controls.docNum"
            label="Document number"
          />
        }
        <app-select
          [options]="payCodeOptions"
          optionLabel="value"
          optionValue="key"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [virtualScroll]="false"
          [control]="fg.controls.payCode"
          label="Pay code"
          [multiple]="false"
          [filter]="false"
          [showClear]="false"
          [showHelpText]="false"
          [disabled]="!enablePaycode"
        />
      </div>
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-0 tw-mt-3 md:tw-mt-0"
      >
        <app-range
          label="Print date"
          [control]="fg.controls.transactionDateStart"
          [toControl]="fg.controls.transactionDateEnd"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [compact]="false"
        ></app-range>
        <app-range
          label="Payment date"
          [control]="fg.controls.serviceDateStart"
          [toControl]="fg.controls.serviceDateEnd"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [compact]="false"
        ></app-range>
      </div>
      <div class="tw-pt-4"></div>
      <app-action-row>
        <app-button
          right1
          type="submit"
          (click)="onSearch()"
          [loading]="loading"
          label="Search"
        />
        <app-button right2 emphasis="Low" (click)="onClear()" label="Clear" />
      </app-action-row>
    </form>
    <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-0">
      <app-radio-button
        [control]="radioTotals"
        label=""
        optionLabel="label"
        optionValue="key"
        [options]="totalOptions"
        [vertical]="false"
        (change)="onRadioTotal()"
        class="tw-w-full md:tw-basis-1/3 tw-min-w-0"
      ></app-radio-button>
    </div>
    <app-detail-panel
      [dataSource]="totals"
      [classes]="
        this.radioTotals.value === 'totalPlus'
          ? 'tw-max-w-[380px]'
          : 'tw-max-w-[220px]'
      "
    >
      @if (this.radioTotals.value === 'total') {
        <app-detail-item
          label="Total"
          name="total"
          format="c"
          classes="tw-font-bold tw-text-lg"
        >
          <ng-template #value let-data>
            <div class="tw-flex tw-items-center tw-gap-2">
              <span>{{ data.total | format: 'c' }}</span>
              <app-help-icon
                helpText="The total represents a sum of the paid or authorization amount."
              />
            </div>
          </ng-template>
        </app-detail-item>
      } @else if (this.radioTotals.value === 'net') {
        <app-detail-item
          label="Net Total"
          name="netTotal"
          format="c"
          classes="tw-font-bold tw-text-lg"
        >
          <ng-template #value let-data>
            <div class="tw-flex tw-items-center tw-gap-2">
              <span>{{ data.netTotal | format: 'c' }}</span>
              <app-help-icon
                helpText="The net total represents a sum of the paid or authorization amount with recoveries subtracted."
              />
            </div>
          </ng-template>
        </app-detail-item>
      } @else if (this.radioTotals.value === 'totalPlus') {
        <app-detail-item
          label="Total + unallocated expense"
          name="totalPlusUnallocated"
          format="c"
          classes="tw-font-bold tw-text-lg"
        >
          <ng-template #value let-data>
            <div class="tw-flex tw-items-center tw-gap-2">
              <span>{{ data.totalPlusUnallocated | format: 'c' }}</span>
              <app-help-icon
                helpText="The total + unallocated expense represents a sum of the paid or authorization amount including unallocated expenses."
              />
            </div>
          </ng-template>
        </app-detail-item>
      }
    </app-detail-panel>

    <app-table
      #tablePayment
      [dataSource]="paymentHistory"
      [pageSize]="10"
      [scrollable]="false"
      [expandable]="true"
      [hideExpandAll]="true"
      [showExport]="true"
      dataKey="paymentuid"
      recordLabel="Payment"
      [stripedRows]="false"
      (export)="onExport($event)"
      (rowExpand)="onPaymentRowExpanded($event)"
      [hideExpandAll]="true"
      [error]="error"
      [globalFilterFields]="[
        'datePrintDisplay',
        'status',
        'statusDisplay',
        'payee',
        'codeDisplay',
        'grossAmountDisplay',
        'adjAmountDisplay',
        'paidAmountDisplay',
        'document',
        'fromDateDisplay',
        'thruDateDisplay',
      ]"
    >
      <!-- <app-column name="paymentuid" label="Id" /> -->
      <app-column name="datePrint" label="Print Date" format="d" />
      <!-- <app-column name="mailDate" label="Mail date" format="d" /> -->
      <app-column name="status" sortName="statusDisplay" label="Status">
        <ng-template #value let-data>
          <app-tag [type]="getTagStyle(data.status)">
            {{ data.statusDisplay }}
          </app-tag>
        </ng-template>
      </app-column>
      <app-column name="payee" label="Payee" classes="tw-font-bold" />
      <app-column name="codeDisplay" label="Code" />
      <app-column name="grossAmount" label="Gross amount" format="c" />
      <app-column name="adjAmount" label="Adj amount" format="c" />
      <app-column name="paidAmount" label="Paid/auth amount" format="c">
        <ng-template #value let-data>
          {{
            getPaidAmountDisplay(data.paidAmount, data.payCode) | format: 'c'
          }}
        </ng-template>
      </app-column>
      <!-- <app-column name="examiner" label="Examiner" /> -->
      <app-column name="document" label="Document" />
      <!-- <app-column name="document" label="Document" /> -->
      <app-column name="fromDate" label="Pay from" format="d" />
      <app-column name="thruDate" label="Pay to" format="d" />
      <ng-template #expand let-data>
        <div class="tw-pt-4"></div>

        <app-tab-panel class="tw-grow" classes="tw-min-h-[360px]">
          <app-tab label="Allocations">
            <ng-template #content>
              <app-table
                #tableAllocation
                [dataSource]="getAllocationsByPaymentUid(data.paymentuid)"
                [scrollable]="true"
                [expandable]="true"
                [hideExpandAll]="true"
                dataKey="allocateUid"
                recordLabel="Allocation"
                [showPaginator]="false"
                [stripedRows]="true"
                [theme]="'Dark'"
                [hideExpandAll]="true"
                [error]="rowExpandError"
                (rowExpand)="onAllocationRowExpanded($event)"
                [globalFilterFields]="[
                  'datePrintDisplay',
                  'status',
                  'statusDisplay',
                  'payCode',
                  'description',
                  'amountDisplay',
                  'datePayFromDisplay',
                  'datePayThruDisplay',
                ]"
              >
                <app-column name="datePrint" label="Print Date" format="d" />
                <!-- <app-column name="mailDate" label="Mail date" format="d" /> -->
                <app-column name="status" sortName="statusDisplay" label="Status">
                  <ng-template #value let-data>
                    <app-tag [type]="getTagStyle(data.status)">
                      {{ data.statusDisplay }}
                    </app-tag>
                  </ng-template>
                </app-column>
                <!-- <app-column name="payee" label="Payee" /> -->
                <app-column name="payCode" label="Code" />
                <app-column
                  label="Description"
                  name="description"
                  class="tw-font-bold"
                >
                </app-column>
                <app-column
                  name="amount"
                  label="Allocation amount"
                  format="c"
                />
                <!-- <app-column name="docType" label="Doc type" /> -->
                <!-- <app-column name="document" label="Document" /> -->
                <app-column name="datePayFrom" label="Pay From" format="d" />
                <app-column name="datePayThru" label="Pay To" format="d" />
                <ng-template #expand let-allocation>
                  <app-tab-panel class="tw-grow">
                    <app-tab label="Allocation">
                      <ng-template #content>
                        <app-payment-allocation-detail
                          [allocateUid]="allocation.allocateUid"
                        ></app-payment-allocation-detail>
                      </ng-template>
                    </app-tab>
                    <app-tab
                      label="Offsets"
                      [visible]="
                        offsetTabVisibilityMap.get(allocation.allocateUid) ??
                        false
                      "
                    >
                      <ng-template #content>
                        <app-payment-allocation-offsets
                          [allocateUid]="allocation.allocateUid"
                          [paymentOffset]="
                            offsetDataMap.get(
                              allocation.allocateUid.toString()
                            ) ?? []
                          "
                        ></app-payment-allocation-offsets>
                      </ng-template>
                    </app-tab>
                  </app-tab-panel>
                </ng-template>
              </app-table>
            </ng-template>
          </app-tab>
          <app-tab label="Payment">
            <ng-template #content>
              <app-payment-detail-tab
                [paymentuid]="data.paymentuid"
                [allocateUid]="data.allocationId"
                [recordSource]="claim.recordSource"
                [recordId]="claim.recordId"
                [workersComp]="workersComp"
                [paymentStatus]="data.status"
              />
            </ng-template>
          </app-tab>
          @if (getEscheatmentData(data.paymentuid)) {
            <app-tab label="Escheatment">
              <ng-template #content>
                <app-escheatment-detail
                  [allocationDetails]="getEscheatmentData(data.paymentuid)"
                />
              </ng-template>
            </app-tab>
          }
          @if (getVendorData(data.paymentuid)) {
            <app-tab label="Vendor payment">
              <ng-template #content>
                <app-vendor-payment-detail
                  [vendor]="getVendorData(data.paymentuid)"
                />
              </ng-template>
            </app-tab>
          }
        </app-tab-panel>
        <div class="tw-pb-[40px]"></div>
      </ng-template>
    </app-table>
  `,
  styles: `
    .payee-wrap {
      word-break: break-word;
      max-width: 220px;
      display: inline-block;
      white-space: normal;
    }
  `,
})
export class PaymentHistoryComponent
  extends BaseComponent
  implements OnInit, AfterViewInit
{
  exportService = inject(ExportService);
  private datePipe = inject(DatePipe);

  claim: ClaimRow;
  historyResponse: HistoryResponse;
  paymentHistory: PaymentHistory[] = [];
  paymentAllocation: Allocation[] = [];
  paymentAllocationTemp: Allocation[] = [];
  paymentAllocationDetails: AllocationDetails[] = [];
  paymentVendor: PaymentVendor[] = [];
  paymentAndAllocateId: PaymentAndAllocateId[] = [];
  offsetTabVisibilityMap = new Map<string, boolean>();
  offsetDataMap = new Map<string, PaymentOffset[]>();
  paymentHistoryColumn: PaymentHistoryColumn[] = [];
  paymentHistoryRow: PaymentHistoryRow[] = [];
  exportData: Export[] = [];

  error: any = null;
  rowExpandError: any = null;

  totalPaidAmount = 0;
  netPaidAmount = 0;
  loading = false;
  enablePaycode = true;
  disability = false;
  workersComp = false;
  enableNetTotal = false;

  // Data storage maps for lazy-loaded data
  vendorDataMap = new Map<string, PaymentVendor | null>();
  escheatmentDataMap = new Map<string, AllocationDetails | null>();
  allocationsByPaymentMap = new Map<string, Allocation[]>();

  fg = new FormGroup({
    provider: new FormControl<ListItem[]>(null),
    payCode: new FormControl<ListItem[]>({
      value: null,
      disabled: !this.enablePaycode,
    }),
    docNum: new FormControl<InputComponent>(null, [
      Validators.maxLength(10),
      integer(),
    ]),
    transactionDateStart: new FormControl<Date>(null),
    transactionDateEnd: new FormControl<Date>(null),
    serviceDateStart: new FormControl<Date>(null),
    serviceDateEnd: new FormControl<Date>(null),
  });

  @Output() export = new EventEmitter<ExportFormat>();

  protected payAlloc = new FormControl<string>('payments');
  protected payAllocOptions: Action[] = [
    { label: 'Payments', key: 'payments' },
    { label: 'Allocations', key: 'allocations' },
  ];
  protected radioTotals = new FormControl<string>('total');
  protected totalOptions: Action[] = [
    { label: 'Total', key: 'total' },
    { label: 'Net total', key: 'net' },
  ];
  payCodeOptions: { key: string; value: string; type: string }[] = [];
  providerOptions: { value: string; key: string }[] = [];

  totals = {
    total: 0,
    netTotal: 0,
    totalPlusUnallocated: 0,
  };

  @HandleLoadingState({ useViewLoading: true })
  async ngOnInit() {
    // Initial setup (immediate)
    this.claim = this.view.currentClaim();
    this.disability = this.claim.lineOfBusiness === 'DS';
    this.workersComp = this.claim.lineOfBusiness === 'WC';

    // Total + Unallocated expense option for WC,AU,GL,PR claims with UnallocatedExpense token
    if (
      (this.workersComp ||
        ['AU', 'GL', 'PR'].includes(this.claim.lineOfBusiness)) &&
      this.session.authorize('UnallocatedExpense')
    ) {
      this.totalOptions.push({
        label: 'Total + Unallocated expense',
        key: 'totalPlus',
      });
    }

    const [historyResponse, paymentAllocationTemp] = await Promise.all([
      this.api.paymentHistory.getPaymentHistory(
        this.claim.recordSource,
        this.claim.recordId,
      ),
      this.api.paymentHistory.getAllocation(
        this.claim.recordSource,
        this.claim.recordId,
      ),
    ]);
    this.historyResponse = historyResponse;
    this.paymentAllocation = paymentAllocationTemp;
    this.paymentAllocationTemp = paymentAllocationTemp;

    this.processAllocationMapping();
    this.paymentHistory = historyResponse.listPaymentHistoryResponse.map(
      (payment) => this.addDisplayFields(payment),
    );

    this.setupProviderOptions();
    this.calculateTotals();
    this.buildAllocationMaps();
  }

  async ngAfterViewInit() {
    this.enablePaycode = false;
  }

  getPaymentAndAllocationIds(): PaymentAndAllocateId[] {
    return this.paymentAllocation.map((item) => ({
      paymentId: item.paymentuid,
      allocateId: item.allocateUid,
    }));
  }

  /**
   * Process allocation mapping to link payments with allocations
   */
  processAllocationMapping(): void {
    if (this.historyResponse && this.paymentAllocation) {
      const allocationMap = new Map<string, string>();
      for (const alloc of this.paymentAllocation) {
        allocationMap.set(alloc.paymentuid, alloc.allocateUid);
      }
      for (const item of this.historyResponse.listPaymentHistoryResponse) {
        if (allocationMap.has(item.paymentuid)) {
          (item as any).allocationId = allocationMap.get(item.paymentuid);
        }
      }
    }
  }

  /**
   * Setup provider options for the dropdown
   */
  setupProviderOptions(): void {
    const staticProviders = [
      { value: 'All payees', key: 'AllPayees' },
      { value: 'Claimant', key: 'NonProviders' },
      { value: 'Other', key: 'AllProviders' },
    ];
    const dynamicProviders = (
      this.historyResponse.listPaymentHistoryProviderResponse || []
    ).map((provider) => ({
      value:
        provider.proTaxId +
        (provider.proLast || provider.proFirst
          ? ' - ' +
            [provider.proLast, provider.proFirst].filter(Boolean).join(' ')
          : ''),
      key: provider.proTaxId,
    }));
    this.providerOptions = [...staticProviders, ...dynamicProviders];
  }

  /**
   * Calculate payment totals for display
   * Uses the current paymentHistory array (which may be filtered)
   */
  calculateTotals(): void {
    let totalPaidAmount = 0;
    let netPaidAmount = 0;
    let totalPlusUnallocatedAmount = 0;

    for (const item of this.paymentHistory) {
      const paidAmount = Number(item.paidAmount) || 0;
      const payCodeNum = Number(item.payCode);

      // Total paid amount (only 'Alloc')
      if (item.unallocType === 'Alloc') {
        totalPaidAmount += paidAmount;

        // Net total: subtract recoveries (pay code series 700)
        if (payCodeNum >= 700 && payCodeNum <= 799) {
          netPaidAmount -= Math.abs(paidAmount);
        } else {
          netPaidAmount += paidAmount;
        }
      }

      // Total plus unallocated (all items)
      totalPlusUnallocatedAmount += paidAmount;
    }

    this.totals = {
      total: totalPaidAmount,
      netTotal: netPaidAmount,
      totalPlusUnallocated: totalPlusUnallocatedAmount,
    };
  }

  @HandleLoadingState({ errorProperty: 'rowExpandError' })
  async onPaymentRowExpanded(event: { data: PaymentHistory }) {
    const allocationId = (event.data as any).allocationId;
    const rowKey = event.data.paymentuid;

    if (this.vendorDataMap.has(rowKey) && this.escheatmentDataMap.has(rowKey)) {
      return;
    }
    const [vendorData, escheatmentData] = await Promise.all([
      this.api.paymentHistory.getVendor(
        this.claim.recordSource,
        this.claim.recordId,
        allocationId,
      ),
      this.api.paymentHistory.getAllocationDetails(
        this.claim.recordSource,
        this.claim.recordId,
        allocationId,
      ),
    ]);

    this.vendorDataMap.set(rowKey, vendorData);
    this.escheatmentDataMap.set(rowKey, escheatmentData);
  }

  @HandleLoadingState({ errorProperty: 'rowExpandError' })
  async onAllocationRowExpanded(event: { data: any }) {
    const uid = String(event.data.allocateUid);

    if (this.offsetDataMap.has(uid)) {
      return;
    }

    const offsets = await this.api.paymentHistory.getOffset(
      this.claim.recordSource,
      this.claim.recordId,
      uid,
    );
    this.offsetDataMap.set(uid, offsets);
    this.offsetTabVisibilityMap.set(
      uid,
      offsets.some(
        (o) =>
          o.adjustmentAction != null ||
          o.adjustmentType != null ||
          o.subType != null ||
          o.source != null ||
          o.amount != null ||
          o.appliedTo != null,
      ),
    );
  }

  /**
   * Get vendor data for a specific payment - returns null if no meaningful data
   */
  getVendorData(paymentuid: string): PaymentVendor | null {
    const vendor = this.vendorDataMap.get(paymentuid);

    if (!vendor) return null;

    // Check if vendor has any non-null fields
    const hasData =
      vendor.payeeType != null ||
      vendor.payeeId != null ||
      vendor.payeeName != null ||
      vendor.paymentDate != null ||
      vendor.paymentType != null ||
      vendor.referenceNumber != null ||
      vendor.vendorCode != null ||
      vendor.voidFlag != null ||
      vendor.amount != null;

    return hasData ? vendor : null;
  }

  /**
   * Get escheatment data for a specific payment - returns null if no meaningful data
   */
  getEscheatmentData(paymentuid: string): AllocationDetails | null {
    const detail = this.escheatmentDataMap.get(paymentuid);

    if (!detail) return null;

    // Check if detail has any escheatment-related fields
    const hasData =
      detail.checkStatus != null ||
      detail.escheatState != null ||
      detail.checkStatusDate != null ||
      detail.dateEscheatState != null;

    return hasData ? detail : null;
  }

  onPayAlloc() {
    this.enablePaycode = this.payAlloc.value === 'allocations';
    if (!this.enablePaycode) {
      this.fg.controls.payCode.setValue(null);
    } else {
      // Start with 'All' static option
      const staticPayCodeOptions = [
        { key: 'all', value: 'All', type: 'Other' },
      ];
      // Add unique series from paymentAllocation as static options
      const uniqueSeries = new Map();
      (this.paymentAllocation || []).forEach((provider) => {
        if (provider.series && !uniqueSeries.has(provider.series)) {
          uniqueSeries.set(provider.series, provider.seriesLabel);
        }
      });
      // uniqueSeries.forEach((seriesLabel, series) => {
      //   staticPayCodeOptions.push({ key: series, value: seriesLabel });
      // });
      let dynamicPayCodeOptions = (this.paymentAllocation || []).map(
        (provider) => ({
          key: provider.payCode,
          value: provider.payCode + ' - ' + provider.description,
          type: 'Other',
        }),
      );

      uniqueSeries.forEach((seriesLabel, series) => {
        dynamicPayCodeOptions.push({
          key: series,
          value: seriesLabel,
          type: 'Series',
        });
      });

      // Remove duplicate keys from dynamicPayCodeOptions
      const seenKeys = new Set();
      dynamicPayCodeOptions = dynamicPayCodeOptions.filter((option) => {
        if (seenKeys.has(option.key)) {
          return false;
        }
        seenKeys.add(option.key);
        return true;
      });
      // Sort dynamicPayCodeOptions by key in ascending order
      dynamicPayCodeOptions = dynamicPayCodeOptions.sort((a, b) => {
        const numA = Number(a.key);
        const numB = Number(b.key);
        if (isNaN(numA) && isNaN(numB)) return 0;
        if (isNaN(numA)) return 1;
        if (isNaN(numB)) return -1;
        return numA - numB;
      });
      this.payCodeOptions = [...staticPayCodeOptions, ...dynamicPayCodeOptions];
    }
  }

  onRadioTotal() {
    // NOTE: when implementing, this value is intended to effect the export calculations
    this.enableNetTotal = this.radioTotals.value === 'net';
  }

  getTagStyle(status: string): TagType {
    let normalized = status ? status.toLocaleLowerCase() : '';
    if (normalized === '1dd') normalized = 'first party deductible';
    if (normalized === '1vd') normalized = 'void first party deductible';
    if (normalized === 'sch') normalized = 'scheduled';
    if (normalized === 'sys') normalized = 'system';
    if (normalized === 'man') normalized = 'manual';
    if (normalized === 'his') normalized = 'historical';
    if (normalized === 'msf') normalized = 'moved system';
    if (normalized === 'mmn') normalized = 'moved manual';
    if (normalized === 'mhs') normalized = 'moved historical';
    if (normalized === 'ref') normalized = 'refund';
    if (normalized === 'mrf') normalized = 'moved refund';
    if (normalized === 'rec') normalized = 'recovery';
    if (normalized === 'mrc') normalized = 'moved recovery';
    if (normalized === 'vsy') normalized = 'void system';
    if (normalized === 'vms') normalized = 'void manual';
    if (normalized === 'vhs') normalized = 'void historical';
    if (normalized === 'vrf') normalized = 'void refund';
    if (normalized === 'vrc') normalized = 'void recovery';
    switch (normalized) {
      case 'refund':
      case 'recovery':
      case 'moved recovery':
      case 'moved refund':
        return 'Purple';
      case 'void refund':
      case 'void system':
      case 'void historical':
      case 'void manual':
      case 'void recovery':
      case 'void first party deductible':
        return 'Warn';
      case 'scheduled':
      case 'schedule':
        return 'Green';
      case 'first party deductible':
        return 'Grey60';
      default:
        return 'Grey';
    }
  }

  getFullStatus(status: string): string {
    switch (status?.toLocaleLowerCase()) {
      case '1dd':
        return 'First party deductible';
      case '1vd':
        return 'Void first party deductible';
      case 'sch':
        return 'Scheduled';
      case 'sys':
        return 'System';
      case 'man':
        return 'Manual';
      case 'his':
        return 'Historical';
      case 'msf':
        return 'Moved system';
      case 'mmn':
        return 'Moved manual';
      case 'mhs':
        return 'Moved historical';
      case 'ref':
        return 'Refund';
      case 'mrf':
        return 'Moved refund';
      case 'rec':
        return 'Recovery';
      case 'mrc':
        return 'Moved recovery';
      case 'vsy':
        return 'Void system';
      case 'vms':
        return 'Void manual';
      case 'vhs':
        return 'Void historical';
      case 'vrf':
        return 'Void refund';
      case 'vrc':
        return 'Void recovery';
      default:
        return status ?? '';
    }
  }

  onSearch() {
    const selectedPayee = this.fg.controls.provider.value;
    const selectedPaycode = this.fg.controls.payCode.value;
    const transactionStart: Date = this.fg.controls.transactionDateStart.value;
    const transactionEnd: Date = this.fg.controls.transactionDateEnd.value;
    const serviceStart: Date = this.fg.controls.serviceDateStart.value;
    const serviceEnd: Date = this.fg.controls.serviceDateEnd.value;
    const selectedRadio = this.payAlloc.value;
    const docNumValue = this.fg.controls.docNum.value;
    if (selectedRadio === 'payments') {
      let filtered = this.filterByPaymentTransactionDate(
        this.historyResponse.listPaymentHistoryResponse.map((payment) =>
          this.addDisplayFields(payment),
        ),
        transactionStart,
        transactionEnd,
      );
      filtered = this.filterByPaymentServiceDate(
        filtered,
        serviceStart,
        serviceEnd,
      );
      filtered = this.filterBySelectedPayee(filtered, selectedPayee);
      filtered = this.filterByDocumentNumber(filtered, docNumValue);
      this.paymentHistory = filtered;

      const allocationFiltered = this.filterAllocationsByPayCodeSeries(
        this.paymentAllocationTemp,
        selectedPaycode,
      );
      this.paymentAllocation = allocationFiltered;
    } else if (selectedRadio === 'allocations') {
      // 1. Filter allocations by paycode first (paycode is on allocation, not payment)
      let filtered = this.filterAllocationsByPayCodeSeries(
        this.paymentAllocationTemp,
        selectedPaycode,
      );
      // 2. Then filter allocations by transaction date and service date
      filtered = this.filterByAllocationTransactionDate(
        filtered,
        transactionStart,
        transactionEnd,
      );
      filtered = this.filterByAllocationServiceDate(
        filtered,
        serviceStart,
        serviceEnd,
      );

      // 3. Get payment IDs from allocations that match the filters (paycode + dates)
      const matchingPaymentIds = filtered.map(
        (allocation) => allocation.paymentuid,
      );

      // 4. Build payment list (payee, doc number) and show only payments that have matching allocations
      let paymentFiltered = this.filterBySelectedPayee(
        this.historyResponse.listPaymentHistoryResponse.map((payment) =>
          this.addDisplayFields(payment),
        ),
        selectedPayee,
      );
      paymentFiltered = this.filterByDocumentNumber(
        paymentFiltered,
        docNumValue,
      );
      this.paymentHistory = paymentFiltered.filter((payment) =>
        matchingPaymentIds.includes(payment.paymentuid),
      );

      this.paymentAllocation = filtered;
    }

    // Recalculate totals based on filtered results
    this.calculateTotals();
    this.buildAllocationMaps();
  }

  filterByPaymentTransactionDate(
    records: any[],
    start: Date,
    end: Date,
  ): any[] {
    if (!start && !end) {
      // No filter, show all
      return records;
    } else if (start && !end) {
      // Only 'From' entered: Print Date >= start
      return records.filter((item) => {
        const printDate = item.datePrint ? new Date(item.datePrint) : null;
        if (!printDate) return false;
        return printDate >= start;
      });
    } else if (!start && end) {
      // Only 'To' entered: Print Date <= end
      return records.filter((item) => {
        const printDate = item.datePrint ? new Date(item.datePrint) : null;
        if (!printDate) return false;
        return printDate <= end;
      });
    } else {
      // Both entered: Print Date in range
      return records.filter((item) => {
        const printDate = item.datePrint ? new Date(item.datePrint) : null;
        if (!printDate) return false;
        return printDate >= start && printDate <= end;
      });
    }
  }

  filterByAllocationTransactionDate(
    records: any[],
    start: Date,
    end: Date,
  ): any[] {
    if (!start && !end) {
      // No filter, show all
      return records;
    } else if (start && !end) {
      // Only 'From' entered: Print Date >= start
      return records.filter((item: any) => {
        const printDate = item.datePrint ? new Date(item.datePrint) : null;
        if (!printDate) return false;
        return printDate >= start;
      });
    } else if (!start && end) {
      // Only 'To' entered: Print Date <= end
      return records.filter((item: any) => {
        const printDate = item.datePrint ? new Date(item.datePrint) : null;
        if (!printDate) return false;
        return printDate <= end;
      });
    } else {
      // Both entered: Print Date in range
      return records.filter((item: any) => {
        const printDate = item.datePrint ? new Date(item.datePrint) : null;
        if (!printDate) return false;
        return printDate >= start && printDate <= end;
      });
    }
  }

  filterByPaymentServiceDate(records: any[], start: Date, end: Date): any[] {
    if (!start && !end) {
      return records;
    } else if (start && !end) {
      // Only 'From' entered: Pay From >= start
      return records.filter((item: any) => {
        const fromDate = item.fromDate ? new Date(item.fromDate) : null;
        if (!fromDate) return false;
        return fromDate >= start;
      });
    } else if (!start && end) {
      // Only 'To' entered: Pay Thru <= end
      return records.filter((item: any) => {
        const thruDate = item.thruDate ? new Date(item.thruDate) : null;
        if (!thruDate) return false;
        return thruDate <= end;
      });
    } else {
      // Both entered: Pay From >= start && Pay Thru <= end
      return records.filter((item: any) => {
        const fromDate = item.fromDate ? new Date(item.fromDate) : null;
        const thruDate = item.thruDate ? new Date(item.thruDate) : null;
        if (!fromDate || !thruDate) return false;
        return fromDate >= start && thruDate <= end;
      });
    }
  }

  filterByAllocationServiceDate(records: any[], start: Date, end: Date): any[] {
    if (!start && !end) {
      return records;
    } else if (start && !end) {
      // Only 'From' entered: Pay From >= start
      return records.filter((item: any) => {
        const fromDate = item.datePayFrom ? new Date(item.datePayFrom) : null;
        if (!fromDate) return false;
        return fromDate >= start;
      });
    } else if (!start && end) {
      // Only 'To' entered: Pay Thru <= end
      return records.filter((item: any) => {
        const thruDate = item.datePayThru ? new Date(item.datePayThru) : null;
        if (!thruDate) return false;
        return thruDate <= end;
      });
    } else {
      // Both entered: Pay From >= start && Pay Thru <= end
      return records.filter((item: any) => {
        const fromDate = item.datePayFrom ? new Date(item.datePayFrom) : null;
        const thruDate = item.datePayThru ? new Date(item.datePayThru) : null;
        if (!fromDate || !thruDate) return false;
        return fromDate >= start && thruDate <= end;
      });
    }
  }

  onClear() {
    this.fg.reset();
    // Optionally, reset dropdowns/options if needed
    this.paymentHistory = (
      this.historyResponse?.listPaymentHistoryResponse || []
    ).map((payment) => this.addDisplayFields(payment));
    this.paymentAllocation = this.paymentAllocationTemp || [];
    this.payCodeOptions = [];
    this.providerOptions = [];
    this.setupProviderOptions();
    this.enablePaycode = false;
    this.enableNetTotal = false;
    this.payAlloc.setValue('payments'); // Reset radio button to 'payments'
    this.radioTotals.setValue('total'); // Reset radio button to 'total'

    // Recalculate totals based on unfiltered data
    this.calculateTotals();
    this.buildAllocationMaps();
  }

  buildAllocationMaps(): void {
    this.allocationsByPaymentMap.clear();

    for (const allocation of this.paymentAllocation) {
      const paymentUid = String(allocation.paymentuid);

      // Add display fields once
      const allocationWithDisplay = {
        ...allocation,
        datePrintDisplay: allocation.datePrint
          ? this.formatDate(allocation.datePrint)
          : '',
        datePayFromDisplay: allocation.datePayFrom
          ? this.formatDate(allocation.datePayFrom)
          : '',
        datePayThruDisplay: allocation.datePayThru
          ? this.formatDate(allocation.datePayThru)
          : '',
        amountDisplay: this.formatAmountForSearch(allocation.amount),
        statusDisplay: this.getFullStatus(allocation.status),
      };

      if (!this.allocationsByPaymentMap.has(paymentUid)) {
        this.allocationsByPaymentMap.set(paymentUid, []);
      }
      this.allocationsByPaymentMap.get(paymentUid).push(allocationWithDisplay);
    }
  }

  getAllocationsByPaymentUid(paymentuid: string) {
    return this.allocationsByPaymentMap.get(String(paymentuid)) || [];
  }

  getPaymentVendorsByAllocationUid(allocationUid: string): PaymentVendor {
    if (!this.paymentVendor || !Array.isArray(this.paymentVendor)) {
      return null;
    }
    const detail =
      this.paymentVendor.find(
        (vendor) => String(vendor.allocationId) === String(allocationUid),
      ) || null;
    return detail;
  }

  getAllocationDetailByAllocationUid(allocationUid: string): AllocationDetails {
    if (
      !this.paymentAllocationDetails ||
      !Array.isArray(this.paymentAllocationDetails)
    ) {
      return null;
    }

    // Check if already cached
    const cached = this.paymentAllocationDetails.find(
      (paymentAllocationDetails) =>
        String(paymentAllocationDetails.allocationId) === String(allocationUid),
    );

    if (!cached) {
      this.loadAllocationDetailAsync(allocationUid);
    }

    return cached || null;
  }

  @HandleLoadingState({ errorProperty: 'rowExpandError' })
  async loadAllocationDetailAsync(allocationUid: string): Promise<void> {
    const detail = await this.api.paymentHistory.getAllocationDetails(
      this.claim.recordSource,
      this.claim.recordId,
      allocationUid,
    );

    // Only add if not already in array
    const exists = this.paymentAllocationDetails.find(
      (d) => String(d.allocationId) === String(allocationUid),
    );

    if (!exists) {
      this.paymentAllocationDetails.push(detail);
    }
  }

  filterBySelectedPayee(records: any[], selectedPayee: any): PaymentHistory[] {
    if (!selectedPayee || selectedPayee.length === 0) {
      return records;
    }
    // Normalize selectedPayee to array of keys
    let payeeKeys: string[] = [];
    if (Array.isArray(selectedPayee)) {
      payeeKeys = selectedPayee.map((p) => (typeof p === 'string' ? p : p.key));
    } else if (typeof selectedPayee === 'string') {
      payeeKeys = [selectedPayee];
    } else if (selectedPayee && selectedPayee.key) {
      payeeKeys = [selectedPayee.key];
    }

    // Special handling for 'AllProviders' (show providerType 'P')
    if (payeeKeys.includes('AllProviders')) {
      return (records || []).filter((item: any) => item.providerType === 'P');
    }
    // Special handling for 'NonProviders' (show providerType 'C' or 'O')
    if (payeeKeys.includes('NonProviders')) {
      return (records || []).filter(
        (item: any) => item.providerType === 'C' || item.providerType === 'O',
      );
    }
    // Special handling for 'AllPayees' (show all)
    if (payeeKeys.includes('AllPayees')) {
      return records;
    }
    // Otherwise, filter by providerType in payeeKeys
    return (records || []).filter((item: any) =>
      payeeKeys.includes(item.providerTaxid),
    );
  }

  filterByDocumentNumber(records: any[], docNum: any): any[] {
    if (!docNum) return records;
    return (records || []).filter((item: any) => {
      return item.document == docNum || docNum == 0;
    });
  }

  filterAllocationsByPayCodeSeries(
    records: any[],
    selectedPayCode: any,
  ): any[] {
    if (!selectedPayCode) return records;
    let code = null;
    if (typeof selectedPayCode === 'string') {
      code = selectedPayCode;
    } else if (selectedPayCode) {
      code = selectedPayCode;
    }
    // If 'all' is selected, return all records
    if (code === 'all') {
      return records;
    }
    const codeNum = Number(code);
    if (isNaN(codeNum)) return records;
    // If codeNum is a multiple of 100, filter by series (e.g., 100-199)
    if (
      (codeNum % 100 === 0 &&
        this.payCodeOptions.find(
          (option) => option.key === code && option.type === 'Series',
        )) ||
      codeNum === 999
    ) {
      return (records || []).filter((item: any) => {
        const itemCode = Number(item.payCode);
        return itemCode >= codeNum && itemCode < codeNum + 100;
      });
    } else {
      // Otherwise, filter by exact payCode
      return (records || []).filter((item: any) => {
        return Number(item.payCode) === codeNum;
      });
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async onExport(format: ExportFormat) {
    const payments = await this.api.paymentHistory.getExport(
      this.claim.recordSource,
      this.claim.recordId,
    );

    // Get payment UIDs from currently filtered/displayed payments
    const filteredPaymentUids = new Set(
      this.paymentHistory.map((payment) => payment.paymentuid),
    );

    // Get allocation UIDs that belong to those filtered payments
    const filteredAllocationUids = new Set(
      this.paymentAllocation
        .filter((allocation) => filteredPaymentUids.has(allocation.paymentuid))
        .map((allocation) => allocation.allocateUid),
    );

    // Filter export data to only include records with matching allocation UIDs
    const filteredPayments = payments.filter((exportItem) =>
      filteredAllocationUids.has(exportItem.allocationId),
    );

    // Sort by datePrint descending
    this.exportData = filteredPayments.sort((a, b) => {
      const dateA = a.datePrint ? new Date(a.datePrint) : new Date(0);
      const dateB = b.datePrint ? new Date(b.datePrint) : new Date(0);
      return dateB.getTime() - dateA.getTime();
    });

    this.paymentHistoryRow = this.exportData.map((exportDataResult: Export) => {
      return {
        printDate: exportDataResult?.datePrint
          ? this.formatDate(exportDataResult.datePrint)
          : '',
        mailDate: exportDataResult?.dateMailed
          ? this.formatDate(exportDataResult.dateMailed)
          : '',
        status: this.getFullStatus(exportDataResult?.payType) || '',
        payee: exportDataResult?.payee || '',
        code: exportDataResult?.paycode || '',
        description: exportDataResult?.paycodeDesc
          ? exportDataResult.paycodeDesc.toString()
          : '',
        paymentAmount:
          exportDataResult?.amount != null ? exportDataResult.amount : '',
        payFrom: exportDataResult?.datePayFrom
          ? this.formatDate(exportDataResult.datePayFrom)
          : '',
        payTo: exportDataResult?.datePayThru
          ? this.formatDate(exportDataResult.datePayThru)
          : '',
        documentType: exportDataResult?.docType
          ? exportDataResult.docType.toString()
          : '',
        bankAccount: exportDataResult?.bankNum
          ? exportDataResult.bankNum.toString()
          : '',
        checkACH: exportDataResult?.checkNum || '',
        checkACHTotal:
          exportDataResult?.paidTotal != null ? exportDataResult.paidTotal : '',
        checkCleared: exportDataResult?.chkCleared == 'Y' ? 'Yes' : 'No',
        checkClearedDate: exportDataResult?.dateClear
          ? this.formatDate(exportDataResult.dateClear)
          : '',
        paymentComments: exportDataResult?.payComments
          ? exportDataResult.payComments.toString()
          : '',
        paymentMethod: exportDataResult?.payVia
          ? exportDataResult.payVia.toString()
          : '',
        dateVoided: exportDataResult?.dateVoid
          ? this.formatDate(exportDataResult.dateVoid)
          : '',
        payeeType: exportDataResult?.nametype
          ? exportDataResult.nametype.toString()
          : '',
        taxID: exportDataResult?.protaxid
          ? this.padLeft(exportDataResult.protaxid.toString())
          : '',
        exempt: exportDataResult?.ind_1099
          ? exportDataResult.ind_1099.toString()
          : '',
        mailingAddress: [
          exportDataResult?.address1,
          exportDataResult?.address2,
          exportDataResult?.address3,
        ]
          .filter((addr) => addr != null && addr !== '')
          .join(' '),
        allocationComments: exportDataResult?.allocComments
          ? exportDataResult.allocComments
          : '',
        allocation: exportDataResult?.allocNum || '',
        earningsCode: exportDataResult?.earnCode
          ? exportDataResult.earnCode
          : '',
        lostWorkDays:
          exportDataResult?.lostWorkDays != null
            ? exportDataResult.lostWorkDays
            : '',
        receivedDate: exportDataResult?.dateBilled
          ? this.formatDate(exportDataResult.dateBilled)
          : '',
        postmark: exportDataResult?.datePostmrk
          ? this.formatDate(exportDataResult.datePostmrk)
          : '',
        invoice: exportDataResult?.binvoice
          ? exportDataResult.binvoice.toString()
          : '',
        amountBilled:
          exportDataResult?.billedAmt != null ? exportDataResult.billedAmt : '',
        vendor: exportDataResult?.vendorId
          ? '\u200B' + exportDataResult.vendorId.toString()
          : '',
        internalControlNumber: exportDataResult?.icn
          ? exportDataResult.icn.toString()
          : '',
        reimbursable: exportDataResult?.reimburseFlag == 'Y' ? 'Yes' : 'No',
        reimbursableCode: exportDataResult?.reimburseCode
          ? exportDataResult.reimburseCode.toString()
          : '',
      };
    });
    const paymentColumns = this.paymentHistoryColumn;
    const columns = [
      {
        propertyName: 'printDate',
        headerText: 'PRINT DATE',
      },
      {
        propertyName: 'mailDate',
        headerText: 'MAIL DATE',
      },
      {
        propertyName: 'status',
        headerText: 'STATUS',
      },
      {
        propertyName: 'payee',
        headerText: 'PAYEE',
      },
      {
        propertyName: 'code',
        headerText: 'PAY CODE',
        format: 'number',
      },
      {
        propertyName: 'description',
        headerText: 'PAY CODE DESCRIPTION',
      },
      {
        propertyName: 'paymentAmount',
        headerText: 'PAYMENT AMOUNT',
        format: 'currency',
      },
      {
        propertyName: 'payFrom',
        headerText: 'PAY FROM',
      },
      {
        propertyName: 'payTo',
        headerText: 'PAY TO',
      },
      {
        propertyName: 'documentType',
        headerText: 'DOCUMENT TYPE',
      },
      {
        propertyName: 'bankAccount',
        headerText: 'BANK ACCOUNT',
      },
      {
        propertyName: 'checkACH',
        headerText: 'CHECK/ACH NUMBER',
        format: 'number',
      },
      {
        propertyName: 'checkACHTotal',
        headerText: 'CHECK/ACH TOTAL',
        format: 'currency',
      },
      {
        propertyName: 'checkCleared',
        headerText: 'CHECK CLEARED',
      },
      {
        propertyName: 'checkClearedDate',
        headerText: 'CHECK CLEARED DATE',
      },
      {
        propertyName: 'paymentComments',
        headerText: 'PAYMENT COMMENTS',
      },
      {
        propertyName: 'paymentMethod',
        headerText: 'PAYMENT METHOD',
      },
      {
        propertyName: 'dateVoided',
        headerText: 'DATE VOIDED',
      },
      {
        propertyName: 'payeeType',
        headerText: 'PAYEE TYPE',
      },
      {
        propertyName: 'taxID',
        headerText: 'TAX ID',
      },
      {
        propertyName: 'exempt',
        headerText: '1099 EXEMPT',
      },
      {
        propertyName: 'mailingAddress',
        headerText: 'MAILING ADDRESS',
      },
      {
        propertyName: 'allocationComments',
        headerText: 'ALLOCATION COMMENT',
      },
      {
        propertyName: 'allocation',
        headerText: 'ALLOCATION',
        format: 'number',
      },
      {
        propertyName: 'earningsCode',
        headerText: 'EARNINGS CODE',
      },
      {
        propertyName: 'lostWorkDays',
        headerText: 'LOST WORK DAYS',
        format: 'number',
      },
      {
        propertyName: 'receivedDate',
        headerText: 'RECEIVED DATE',
      },
      {
        propertyName: 'postmark',
        headerText: 'POSTMARK',
      },
      {
        propertyName: 'invoice',
        headerText: 'INVOICE',
      },
      {
        propertyName: 'amountBilled',
        headerText: 'AMOUNT BILLED',
        format: 'currency',
      },
      {
        propertyName: 'vendor',
        headerText: 'VENDOR',
      },
      {
        propertyName: 'internalControlNumber',
        headerText: 'INTERNAL CONTROL NUMBER',
      },
      {
        propertyName: 'reimbursable',
        headerText: 'REIMBURSABLE',
      },
      {
        propertyName: 'reimbursableCode',
        headerText: 'REIMBURSABLE CODE',
      },
      ...paymentColumns,
    ];
    const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
    const filename = `Payment_History_${formattedDate}.${format}`;
    if (format === 'csv') {
      const csvData = ClaimExportMapper.toCsvExport(
        this.paymentHistoryRow,
        columns,
        'Payment_History',
      );
      await this.exportService.exportCsv(csvData, filename);
    } else if (format === 'xlsx') {
      const excelData = ClaimExportMapper.toExcelExport(
        this.paymentHistoryRow,
        columns,
        'Payment_History',
        'OmniInsights Export',
      );
      await this.exportService.exportExcel(excelData, filename);
    }
    this.export.emit(format);
  }

  formatDate(date: string | Date): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  /**
   * Format currency amount for search - normalizes to consistent decimal format
   * Converts all variations to include multiple searchable formats:
   * - "0" -> "0|0.00|0.00"
   * - "47691.51" -> "47691.51|47691.51|47,691.51|$47,691.51"
   * This allows searching "0", "0.00", "47691.51", "47,691.51", "$47,691.51" to all match correctly
   * Matches the format that AggregatedSearchService uses for numbers
   */
  formatAmountForSearch(amount: string | number | null | undefined): string {
    if (amount == null || amount === '') return '';

    // Convert to string and strip any non-numeric except decimal and minus
    const cleanValue =
      typeof amount === 'string'
        ? amount.replace(/[^0-9.-]/g, '')
        : amount.toString();

    // Parse as number
    const numValue = parseFloat(cleanValue);
    if (isNaN(numValue)) return '';

    // Return multiple variations separated by pipe for flexible matching
    // This matches what AggregatedSearchService does for numbers (lines 53-63)
    const rawNumber = numValue.toString();
    const withDecimals = numValue.toFixed(2);
    const withCommas = numValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    // Return all formats including $-prefixed: "47691.51|47691.51|47,691.51|$47,691.51"
    return `${rawNumber}|${withDecimals}|${withCommas}|$${withCommas}`;
  }

  /**
   * Add display fields for dates and currency amounts to enable proper global filtering
   */
  addDisplayFields(payment: any): any {
    return {
      ...payment,
      // Date display fields (MM/dd/yyyy format without time)
      datePrintDisplay: payment.datePrint
        ? this.formatDate(payment.datePrint)
        : '',
      fromDateDisplay: payment.fromDate
        ? this.formatDate(payment.fromDate)
        : '',
      thruDateDisplay: payment.thruDate
        ? this.formatDate(payment.thruDate)
        : '',
      // Currency display fields (numeric strings without $ symbol)
      grossAmountDisplay: this.formatAmountForSearch(payment.grossAmount),
      adjAmountDisplay: this.formatAmountForSearch(payment.adjAmount),
      paidAmountDisplay: this.formatAmountForSearch(payment.paidAmount),
      // Code column: show payCodeDisplay when payment has multiple pay codes (e.g. "Disability", "Multiple"), else numeric payCode
      codeDisplay: payment.payCodeDisplay ?? payment.payCode ?? '',
      statusDisplay: this.getFullStatus(payment.status),
    };
  }

  getPaymentTypeFromHistory(paymentUid: number): string {
    const history = this.paymentHistory.find(
      (ph) => String(ph.paymentuid) === String(paymentUid),
    );
    return history ? history.status : '';
  }

  /**
   * Get the paid amount display value, applying negative formatting for
   * pay code series 700 (recoveries) when Net total is selected
   */
  getPaidAmountDisplay(paidAmount: number, payCode: string): number {
    if (!this.enableNetTotal) {
      return paidAmount;
    }

    const payCodeNum = Number(payCode);
    // Check if pay code is in the 700 series (700-799)
    if (payCodeNum >= 700 && payCodeNum <= 799) {
      return -Math.abs(paidAmount);
    }

    return paidAmount;
  }

  padLeft(value: string | number): string {
    const [before, after] = String(value).split('-');
    const paddedBefore = String(before).padStart(9, '0');
    const paddedAfter = String(after).padStart(3, '0');
    return `${paddedBefore}-${paddedAfter}`;
  }
}
